import type { DriveStep } from "driver.js";

import { runTour, type TourOutcome } from "../tour/runTour";

/**
 * The project listing coach tour: names the two panes and the three ideas
 * people don't guess (hover links the panes, folders exist, the old layout is
 * one click away). Anchors are explicit `data-tour` attributes, never MUI
 * class names (those are Emotion hashes).
 */
export const LISTING_TOUR_ANCHOR = {
  map: "listing-map",
  table: "listing-table",
  breadcrumbs: "folder-breadcrumbs",
  newMenu: "new-menu",
  browseToggle: "browse-toggle",
  expand: "map-expand",
  layoutToggle: "layout-toggle",
  help: "listing-help",
} as const;

const at = (anchor: string) => `[data-tour="${anchor}"]`;

export const LISTING_TOUR_STEPS: DriveStep[] = [
  {
    element: at(LISTING_TOUR_ANCHOR.map),
    popover: {
      title: "Your projects, on a map",
      description:
        "Every project in the folder you're looking at is drawn here. Point " +
        "at one and its row lights up in the list; rest on it a moment and " +
        "the list scrolls to it.",
      side: "right",
      align: "center",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.table),
    popover: {
      title: "…and in a list",
      description:
        "The same projects as a list. Point at a row and the map highlights " +
        "it; the target button flies the map to it. Click a row to open it, " +
        "or click a column heading to sort.",
      side: "left",
      align: "start",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.breadcrumbs),
    popover: {
      title: "Folders keep things tidy",
      description:
        "Group projects into folders, and folders inside folders. Click a " +
        "folder to look inside and use this trail to step back out. Drag a " +
        "project onto a folder to move it, and point at a folder to see the " +
        "area it covers on the map.",
      side: "bottom",
      align: "start",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.newMenu),
    popover: {
      title: "Create things here",
      description:
        "Start a new project or make a new folder. New projects go into the " +
        "folder you're looking at.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.browseToggle),
    popover: {
      title: "Story maps live here too",
      description:
        "Switch to Story maps to browse every story in the same folders. " +
        "Each project with a story shows its cover on the map; click one to " +
        "open it.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.expand),
    popover: {
      title: "Need a bigger map?",
      description:
        "This makes the map fill the screen. Press it again, or the Escape " +
        "key, to bring the list back. You can also drag the divider between " +
        "the map and the list.",
      side: "left",
      align: "start",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.layoutToggle),
    popover: {
      title: "Prefer the old layout?",
      description:
        "This switches back to the previous tiles-and-map page. It stays that " +
        "way until you switch again, and you can come back here whenever you " +
        "like.",
      side: "bottom",
      align: "end",
    },
  },
  {
    element: at(LISTING_TOUR_ANCHOR.help),
    popover: {
      title: "That's the tour",
      description: "Replay it any time from here.",
      side: "bottom",
      align: "end",
    },
  },
];

export function runListingTour(onFinish: (outcome: TourOutcome) => void): () => void {
  return runTour(LISTING_TOUR_STEPS, onFinish);
}
